import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Clock, 
  CheckCircle2, 
  FileSpreadsheet,
  Zap,
  Flame,
  AlertTriangle,
  Home,
  Award,
  Building2,
  Ruler,
  Droplets,
  FileCheck,
  Star,
  Calendar,
  Euro,
  Thermometer,
  Wind
} from 'lucide-react';

const DiagnosticArdeche = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const villesArdeche = [
    "La Voulte-sur-Rhône 07800", "Rompon 07250", "Beauchastel 07800",
    "Saint-Georges-les-Bains 07800", "Charmes 07800", "Toulaud 07130",
    "Guilherand-Granges 07500", "Saint-Péray 07130"
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-32 pb-16 px-4 bg-gradient-to-br from-white to-gray-100"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Hero Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.div variants={itemVariants} className="flex justify-center mb-6">
            <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue p-4 rounded-full">
              <MapPin className="h-12 w-12 text-white" />
            </div>
          </motion.div>
          <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Diagnostic Immobilier Ardèche (07) - Saint-Péray, La Voulte-sur-Rhône
          </motion.h1>
          <motion.p variants={itemVariants} className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Expert certifié en diagnostics immobiliers dans l'Ardèche. Intervention à Saint-Péray, Guilherand-Granges, 
            La Voulte-sur-Rhône, Toulaud et dans toute la vallée du Rhône ardéchoise.
          </motion.p>
        </motion.div>

        {/* Article Content */}
        <motion.article
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="prose prose-lg max-w-none"
        >
          {/* Introduction */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <Award className="h-8 w-8 text-dk-blue mr-4" />
              Diagnostics Immobiliers en Ardèche : Votre Expert de Proximité
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              L'<strong>Ardèche (07)</strong> offre un cadre de vie exceptionnel entre vallée du Rhône et montagnes. 
              De <strong>Saint-Péray</strong>, réputée pour ses vins effervescents, à <strong>La Voulte-sur-Rhône</strong> 
              avec son patrimoine industriel, en passant par <strong>Guilherand-Granges</strong> et ses quartiers résidentiels, 
              chaque transaction immobilière nécessite des diagnostics conformes aux dernières réglementations.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              <strong>DIAGNOSTI-K</strong>, votre diagnostiqueur immobilier certifié, intervient dans toute l'Ardèche 
              pour accompagner propriétaires, acquéreurs et professionnels de l'immobilier. Basé à proximité, 
              nous nous déplaçons rapidement sur <strong>Saint-Péray 07130</strong>, <strong>Guilherand-Granges 07500</strong>, 
              <strong>La Voulte-sur-Rhône 07800</strong> et dans l'ensemble des communes ardéchoises de la vallée du Rhône.
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircle2 className="h-6 w-6 text-dk-blue mr-3" />
                Pourquoi choisir DIAGNOSTI-K en Ardèche ?
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Star className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Expertise locale</strong> : Connaissance parfaite du territoire ardéchois</span>
                </li>
                <li className="flex items-start">
                  <Clock className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Réactivité</strong> : Intervention sous 48h dans toute l'Ardèche</span>
                </li>
                <li className="flex items-start">
                  <FileCheck className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Qualité</strong> : Rapports détaillés conformes aux normes</span>
                </li>
                <li className="flex items-start">
                  <Euro className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Transparence</strong> : Devis gratuit et tarifs compétitifs</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Services détaillés */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-8 flex items-center">
              <FileSpreadsheet className="h-8 w-8 mr-4" />
              Nos Services de Diagnostic en Ardèche
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <FileSpreadsheet className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">DPE Saint-Péray et Ardèche</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Le <strong>Diagnostic de Performance Énergétique</strong> est obligatoire pour toute vente ou location 
                  en Ardèche. Nous réalisons vos DPE à Saint-Péray, Guilherand-Granges, La Voulte-sur-Rhône et dans 
                  toutes les communes du 07.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Évaluation énergétique complète</li>
                  <li>• Classement de A à G</li>
                  <li>• Recommandations personnalisées</li>
                  <li>• Validité 10 ans</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Zap className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Électricité Ardèche</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Vérification complète des installations électriques dans les logements de plus de 15 ans 
                  à Toulaud, Beauchastel, Saint-Georges-les-Bains et dans toute l'Ardèche.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Contrôle de conformité NF C 15-100</li>
                  <li>• Détection des anomalies</li>
                  <li>• Sécurité des occupants</li>
                  <li>• Validité 3 ans (vente) / 6 ans (location)</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Flame className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Gaz Ardèche</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Contrôle sécuritaire des installations gaz dans les logements ardéchois équipés d'installations 
                  de plus de 15 ans. Intervention à Rompon, Charmes et environs.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Vérification des canalisations</li>
                  <li>• Test d'étanchéité</li>
                  <li>• Contrôle des appareils</li>
                  <li>• Prévention des risques</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Amiante Ardèche</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Recherche d'amiante obligatoire dans les bâtiments construits avant 1997 en Ardèche. 
                  Expertise dans toute la vallée du Rhône ardéchoise.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Repérage des matériaux amiantés</li>
                  <li>• Évaluation de l'état de conservation</li>
                  <li>• Recommandations de gestion</li>
                  <li>• Protection de la santé</li>
                </ul>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Autres diagnostics disponibles en Ardèche :</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center">
                  <Home className="h-5 w-5 mr-2" />
                  <span>Termites</span>
                </div>
                <div className="flex items-center">
                  <Droplets className="h-5 w-5 mr-2" />
                  <span>Plomb (CREP)</span>
                </div>
                <div className="flex items-center">
                  <Ruler className="h-5 w-5 mr-2" />
                  <span>Carrez / Boutin</span>
                </div>
                <div className="flex items-center">
                  <FileCheck className="h-5 w-5 mr-2" />
                  <span>ERP</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Zone d'intervention */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <MapPin className="h-8 w-8 text-dk-blue mr-4" />
              Zone d'Intervention en Ardèche
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              Notre diagnostiqueur certifié intervient dans l'ensemble de l'<strong>Ardèche (07)</strong>, 
              particulièrement dans la vallée du Rhône. Que vous soyez à Saint-Péray, Guilherand-Granges, 
              ou dans les communes rurales, nous nous déplaçons rapidement pour réaliser vos diagnostics immobiliers.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Building2 className="h-6 w-6 text-dk-blue mr-3" />
                  Vallée du Rhône Nord
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Saint-Péray 07130</strong> - Ville viticole</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Guilherand-Granges 07500</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Toulaud 07130</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Saint-Georges-les-Bains 07800</strong></span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Home className="h-6 w-6 text-dk-blue mr-3" />
                  Vallée du Rhône Sud
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>La Voulte-sur-Rhône 07800</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Beauchastel 07800</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Rompon 07250</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Charmes 07800</strong></span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Toutes nos communes d'intervention en Ardèche :</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {villesArdeche.map((ville, index) => (
                  <div key={index} className="flex items-center">
                    <MapPin className="h-4 w-4 text-dk-blue mr-2 flex-shrink-0" />
                    <span className="font-medium">{ville}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Spécificités locales */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Award className="h-8 w-8 mr-4" />
              Spécificités du Marché Immobilier Ardéchois
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white/10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">Patrimoine Viticole</h3>
                <p className="text-white/90 mb-4">
                  L'Ardèche, notamment <strong>Saint-Péray</strong> et <strong>Toulaud</strong>, possède un riche 
                  patrimoine viticole avec des propriétés anciennes nécessitant des diagnostics spécialisés.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Diagnostic amiante pour bâtiments anciens</li>
                  <li>• DPE adapté aux constructions traditionnelles</li>
                  <li>• Expertise des caves et dépendances</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">Développement Résidentiel</h3>
                <p className="text-white/90 mb-4">
                  <strong>Guilherand-Granges</strong> et les communes de la vallée connaissent un développement 
                  résidentiel important avec de nouveaux programmes immobiliers.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• DPE pour constructions neuves</li>
                  <li>• Diagnostics avant travaux</li>
                  <li>• Audits énergétiques</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Processus et délais */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <Clock className="h-8 w-8 text-dk-blue mr-4" />
              Notre Processus en Ardèche
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Contact</h3>
                <p className="text-gray-600 text-sm">
                  Appelez-nous ou demandez un devis en ligne. Réponse rapide pour toute l'Ardèche.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Intervention</h3>
                <p className="text-gray-600 text-sm">
                  Déplacement sous 48h à Saint-Péray, La Voulte-sur-Rhône ou dans votre commune.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Rapport</h3>
                <p className="text-gray-600 text-sm">
                  Remise des diagnostics sous 72h maximum par email et courrier.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-6 w-6 text-dk-blue mr-3" />
                Horaires d'Intervention en Ardèche
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-gray-900">Lundi - Vendredi</p>
                  <p className="text-gray-700">8h00 - 19h00</p>
                  <p className="text-sm text-gray-600">Interventions sur rendez-vous</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Samedi</p>
                  <p className="text-gray-700">Sur rendez-vous</p>
                  <p className="text-sm text-gray-600">Selon disponibilités</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Besoin d'un Diagnostic en Ardèche ?</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Contactez DIAGNOSTI-K pour tous vos diagnostics immobiliers à Saint-Péray, Guilherand-Granges, 
              La Voulte-sur-Rhône et dans toute l'Ardèche. Intervention rapide et tarifs transparents.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href="tel:0789307500"
                className="btn bg-white text-dk-blue hover:bg-gray-100 text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="h-5 w-5 mr-2" />
                07 89 30 75 00
              </a>
              <Link
                to="/contact"
                className="btn bg-white/10 hover:bg-white/20 text-white border-2 border-white text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Demander un devis
              </Link>
            </div>
          </motion.div>
        </motion.article>
      </div>
    </motion.div>
  );
};

export default DiagnosticArdeche;